import React from 'react';
import { Building2 } from 'lucide-react';

/** reference: "(산업안전과-3105, 2021.6.22.)" → 표시용 문서번호 문자열 */
export const formatQaReference = (reference) => {
  if (!reference || !String(reference).trim()) return null;
  const raw = String(reference).trim();
  const inner = raw.replace(/^\(|\)\.?$/g, '').trim();
  return inner || raw;
};

/** 괄호 안 문서번호 등을 구글 검색 버튼으로 표시 */
const ReferenceGoogleSearch = ({ referenceText }) => {
  if (!referenceText || !String(referenceText).trim()) return null;

  const text = String(referenceText).trim();
  const regex = /\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  let hasBracket = false;

  while ((match = regex.exec(text)) !== null) {
    hasBracket = true;
    if (match.index > lastIndex) {
      parts.push(
        <span key={`text-${lastIndex}`}>{text.substring(lastIndex, match.index)}</span>
      );
    }
    const bracketText = match[1];
    const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(bracketText)}`;
    parts.push(
      <a
        key={`link-${match.index}`}
        href={googleSearchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md"
        title={`"${bracketText}" 구글에서 검색하기`}
      >
        <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {bracketText}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  if (hasBracket) {
    if (lastIndex < text.length) {
      parts.push(<span key="text-last">{text.substring(lastIndex)}</span>);
    }
    return <div className="shrink-0 flex flex-wrap items-center justify-end gap-2">{parts}</div>;
  }

  const docRef = formatQaReference(text);
  if (!docRef) return null;
  return (
    <a
      href={`https://www.google.com/search?q=${encodeURIComponent(docRef)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="shrink-0 inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-sm hover:shadow-md whitespace-nowrap"
      title={`"${docRef}" 구글에서 검색하기`}
    >
      <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {docRef}
    </a>
  );
};

/**
 * 질의회시 출처 — 고용노동부 행정해석 원문 (저작권 없음, 출처·문서번호 명시)
 */
const QASourceAttribution = ({ reference, isConstruction = false, isHeat = false, variant = 'full', className = '' }) => {
  const docRef = formatQaReference(reference);
  const sourceTitle = isHeat
    ? '고용노동부 · 폭염 관련 산업안전보건규칙 질의회시집(2026.5)'
    : isConstruction
      ? '고용노동부 · 건설업 산업안전보건관리비 질의회시'
      : '고용노동부 질의회시';

  if (variant === 'compact') {
    return (
      <p className={`text-xs text-gray-600 leading-relaxed ${className}`}>
        본 내용은 <strong>{sourceTitle}</strong> 행정해석 원문입니다.
        {docRef && (
          <>
            {' '}
            문서번호: <span className="font-medium text-gray-800">{docRef}</span>
          </>
        )}
      </p>
    );
  }

  return (
    <aside
      className={`rounded-lg border border-slate-300 bg-slate-50 p-4 sm:p-5 ${className}`}
      role="note"
      aria-label="질의회시 출처"
    >
      <div className="flex items-start gap-3">
        <Building2 className="w-5 h-5 text-slate-700 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0 flex-1 text-sm text-slate-800 leading-relaxed">
          <p className="font-semibold text-slate-900">
            출처: {sourceTitle}
            {docRef && (
              <span className="font-normal text-slate-600">
                {' '}
                · 문서번호: <span className="font-medium text-slate-900">{docRef}</span>
              </span>
            )}
          </p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-slate-700 leading-snug flex-1 min-w-0">
              본 문서는 고용노동부에서 공개한 <strong>행정해석(질의·회시) 원문</strong>을 제공합니다.
            </p>
            <ReferenceGoogleSearch referenceText={reference} />
          </div>
        </div>
      </div>
    </aside>
  );
};

export default QASourceAttribution;
